import { useState, useEffect, useRef } from 'react';
import { Download, Smartphone, Monitor, Tv, Link2, QrCode, FileArchive, Save, Upload, Plus, Loader2, CheckCircle2, X } from 'lucide-react';
import { appApi, downloadApi } from '@/api/client';
import type { App, DownloadConfig, DownloadPlatform } from '@/types';

const PLATFORMS: { key: DownloadPlatform; label: string; icon: typeof Smartphone }[] = [
  { key: 'app', label: 'App 端', icon: Smartphone },
  { key: 'pc', label: 'PC 端', icon: Monitor },
  { key: 'tv', label: 'TV 端', icon: Tv },
];

function formatSize(bytes: number): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

interface EditingState {
  enabled: boolean;
  mode_url_enabled: boolean;
  mode_qrcode_enabled: boolean;
  mode_file_enabled: boolean;
  url: string;
  qrcode_r2_key: string;
  qrcode_url: string;
  file_r2_key: string;
  file_name: string;
  file_size: number;
  file_version: string;
}

function toEditing(c: DownloadConfig | undefined): EditingState {
  return {
    enabled: !!c?.enabled,
    mode_url_enabled: !!c?.mode_url_enabled,
    mode_qrcode_enabled: !!c?.mode_qrcode_enabled,
    mode_file_enabled: !!c?.mode_file_enabled,
    url: c?.url || '',
    qrcode_r2_key: c?.qrcode_r2_key || '',
    qrcode_url: c?.qrcode_url || '',
    file_r2_key: c?.file_r2_key || '',
    file_name: c?.file_name || '',
    file_size: c?.file_size || 0,
    file_version: c?.file_version || '',
  };
}

export default function Downloads() {
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [configs, setConfigs] = useState<DownloadConfig[]>([]);
  const [editing, setEditing] = useState<Record<DownloadPlatform, EditingState>>({
    app: toEditing(undefined),
    pc: toEditing(undefined),
    tv: toEditing(undefined),
  });
  const [saving, setSaving] = useState<Record<DownloadPlatform, boolean>>({ app: false, pc: false, tv: false });
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [loading, setLoading] = useState(true);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await appApi.getAll();
      setApps(res.data || []);
      if (res.data && res.data.length > 0) setSelectedAppId(res.data[0].id);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedAppId) return;
    (async () => {
      const res = await downloadApi.getAll(selectedAppId);
      const list = res.data || [];
      setConfigs(list);
      const map: Record<DownloadPlatform, EditingState> = {
        app: toEditing(list.find((c) => c.platform === 'app')),
        pc: toEditing(list.find((c) => c.platform === 'pc')),
        tv: toEditing(list.find((c) => c.platform === 'tv')),
      };
      setEditing(map);
    })();
  }, [selectedAppId]);

  const updateEditing = (platform: DownloadPlatform, patch: Partial<EditingState>) => {
    setEditing((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));
  };

  const savePlatform = async (platform: DownloadPlatform) => {
    if (!selectedAppId) return;
    setSaving((s) => ({ ...s, [platform]: true }));
    const e = editing[platform];
    const existing = configs.find((c) => c.platform === platform);
    // editing 中开关为 boolean，API 期望 0/1
    const payload = {
      enabled: e.enabled ? 1 : 0,
      mode_url_enabled: e.mode_url_enabled ? 1 : 0,
      mode_qrcode_enabled: e.mode_qrcode_enabled ? 1 : 0,
      mode_file_enabled: e.mode_file_enabled ? 1 : 0,
      url: e.url,
      qrcode_r2_key: e.qrcode_r2_key,
      qrcode_url: e.qrcode_url,
      file_r2_key: e.file_r2_key,
      file_name: e.file_name,
      file_size: e.file_size,
      file_version: e.file_version,
    };
    try {
      if (existing) {
        await downloadApi.update(existing.id, payload);
      } else {
        const res = await downloadApi.create({ app_id: selectedAppId, platform, ...payload } as any);
        if (res.data) {
          setConfigs((prev) => [...prev, res.data]);
        }
      }
      showToast(`${PLATFORMS.find((p) => p.key === platform)?.label} 配置已保存`);
    } catch (err) {
      showToast('保存失败，请重试', false);
    } finally {
      setSaving((s) => ({ ...s, [platform]: false }));
    }
  };

  const handleUpload = async (platform: DownloadPlatform, file: File, type: 'qrcode' | 'file') => {
    if (!selectedAppId) return;
    const key = `${platform}-${type}`;
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const res = await downloadApi.upload(file, selectedAppId, platform, type);
      if (res.success && res.data) {
        if (type === 'qrcode') {
          updateEditing(platform, { qrcode_r2_key: res.data.r2_key, qrcode_url: res.data.url });
        } else {
          updateEditing(platform, { file_r2_key: res.data.r2_key, file_name: file.name, file_size: file.size });
        }
        showToast('上传成功');
      } else {
        showToast(res.message || '上传失败', false);
      }
    } catch (err) {
      showToast('上传失败', false);
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (apps.length === 0) {
    return (
      <div className="bg-slate-800 rounded-xl p-8 text-center text-slate-400">
        <Download className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>请先在「应用管理」中创建应用，再配置客户端下载</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Download className="w-7 h-7 text-blue-400" />
            客户端下载管理
          </h2>
          <p className="text-slate-400 text-sm mt-1">为每个应用配置 App / PC / TV 三端客户端下载，支持三种模式独立开关</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-slate-400 text-sm">选择应用</label>
          <select
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
            className="bg-slate-700 text-white border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
          >
            {apps.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {PLATFORMS.map((p) => {
          const e = editing[p.key];
          const Icon = p.icon;
          const isSaving = saving[p.key];
          return (
            <div key={p.key} className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-5 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">{p.label}</div>
                    <div className="text-xs text-slate-500">平台总开关</div>
                  </div>
                </div>
                <button
                  onClick={() => updateEditing(p.key, { enabled: !e.enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${e.enabled ? 'bg-blue-600' : 'bg-slate-600'}`}
                  title="启用/禁用该平台"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${e.enabled ? 'translate-x-6' : ''}`} />
                </button>
              </div>

              <div className="p-5 space-y-4 flex-1">
                {/* 模式1：网址直达 */}
                <ModeBlock
                  icon={Link2}
                  color="text-emerald-400"
                  title="网址直达"
                  desc="点击直接跳转到指定 URL"
                  enabled={e.mode_url_enabled}
                  onToggle={() => updateEditing(p.key, { mode_url_enabled: !e.mode_url_enabled })}
                >
                  <input
                    type="url"
                    value={e.url}
                    onChange={(ev) => updateEditing(p.key, { url: ev.target.value })}
                    placeholder="https://example.com/download/app.apk"
                    className="w-full bg-slate-900 text-white text-sm border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </ModeBlock>

                {/* 模式2：二维码扫码 */}
                <ModeBlock
                  icon={QrCode}
                  color="text-purple-400"
                  title="二维码扫码"
                  desc="上传二维码图片，用户扫码下载"
                  enabled={e.mode_qrcode_enabled}
                  onToggle={() => updateEditing(p.key, { mode_qrcode_enabled: !e.mode_qrcode_enabled })}
                >
                  <UploadBox
                    accept="image/*"
                    uploading={uploading[`${p.key}-qrcode`]}
                    onFile={(f) => handleUpload(p.key, f, 'qrcode')}
                    label="上传二维码图片"
                  />
                  {e.qrcode_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={e.qrcode_url} alt="二维码预览" className="w-16 h-16 rounded-lg object-contain bg-white p-1" />
                      <button
                        onClick={() => updateEditing(p.key, { qrcode_r2_key: '', qrcode_url: '' })}
                        className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> 移除
                      </button>
                    </div>
                  )}
                </ModeBlock>

                {/* 模式3：文件下载 */}
                <ModeBlock
                  icon={FileArchive}
                  color="text-amber-400"
                  title="文件下载"
                  desc="上传安装包到存储，通过 API 下载"
                  enabled={e.mode_file_enabled}
                  onToggle={() => updateEditing(p.key, { mode_file_enabled: !e.mode_file_enabled })}
                >
                  <input
                    type="text"
                    value={e.file_version}
                    onChange={(ev) => updateEditing(p.key, { file_version: ev.target.value })}
                    placeholder="版本号，如 v1.0.0"
                    className="w-full bg-slate-900 text-white text-sm border border-slate-600 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  />
                  <UploadBox
                    accept=".apk,.exe,.dmg,.pkg,.ipa,.zip,.rar,.7z,application/*"
                    uploading={uploading[`${p.key}-file`]}
                    onFile={(f) => handleUpload(p.key, f, 'file')}
                    label="上传安装包"
                  />
                  {e.file_name && (
                    <div className="mt-2 flex items-center justify-between bg-slate-900 rounded-lg px-3 py-2">
                      <div className="text-xs text-slate-300 truncate">
                        <div className="text-white">{e.file_name}</div>
                        <div className="text-slate-500">{formatSize(e.file_size)}{e.file_version ? ` · ${e.file_version}` : ''}</div>
                      </div>
                      <button
                        onClick={() => updateEditing(p.key, { file_r2_key: '', file_name: '', file_size: 0 })}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </ModeBlock>
              </div>

              <div className="p-5 border-t border-slate-700">
                <button
                  onClick={() => savePlatform(p.key)}
                  disabled={isSaving}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  保存{p.label}配置
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* SDK 接入提示 */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="w-5 h-5 text-blue-400" />
          <h3 className="text-white font-semibold">前端接入方式</h3>
        </div>
        <p className="text-slate-400 text-sm mb-3">在各程序前端首页 <code className="text-yellow-300 bg-slate-900 px-1 rounded">&lt;body&gt;</code> 中引入以下代码，即可在左上方自动显示下载下拉窗口（仅显示已启用并配置的模式）：</p>

        <div className="mb-2 text-xs text-slate-400">方式 A：脚本标签自动初始化（推荐，最简单）</div>
        <pre className="bg-slate-900 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto mb-4"><code>{`<script
  src="https://unified-auth-admin.pages.dev/sdk/download-widget.js"
  data-app-id="${selectedAppId || 'YOUR_APP_ID'}"
  defer
></script>`}</code></pre>

        <div className="mb-2 text-xs text-slate-400">方式 B：手动初始化（可自定义位置与时机）</div>
        <pre className="bg-slate-900 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto mb-4"><code>{`<script src="https://unified-auth-admin.pages.dev/sdk/download-widget.js"></script>
<script>
  new DownloadWidget({
    appId: '${selectedAppId || 'YOUR_APP_ID'}',
    apiBase: 'https://unified-auth-admin.pages.dev',
    position: 'top-left'
  }).init();
</script>`}</code></pre>

        <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
          <p className="text-slate-500 text-xs">公开数据接口：<code className="text-blue-400">GET /api/downloads/public/{selectedAppId || '{app_id}'}</code></p>
          <p className="text-slate-500 text-xs">文件下载接口：<code className="text-blue-400">GET /api/downloads/file/&#123;file_id&#125;</code></p>
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${toast.ok ? 'bg-emerald-600' : 'bg-red-600'} text-white`}>
          {toast.ok ? <CheckCircle2 className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function ModeBlock({ icon: Icon, color, title, desc, enabled, onToggle, children }: {
  icon: typeof Link2;
  color: string;
  title: string;
  desc: string;
  enabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border transition-colors ${enabled ? 'border-slate-600 bg-slate-900/50' : 'border-slate-700 bg-slate-900/20'}`}>
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <div>
            <div className="text-sm font-medium text-white">{title}</div>
            <div className="text-xs text-slate-500">{desc}</div>
          </div>
        </div>
        <button
          onClick={onToggle}
          className={`relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-slate-600'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>
      {enabled && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function UploadBox({ accept, uploading, onFile, label }: {
  accept: string;
  uploading: boolean;
  onFile: (f: File) => void;
  label: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-600 hover:border-blue-500 text-slate-400 hover:text-blue-400 rounded-lg py-3 text-sm transition-colors disabled:opacity-50"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? '上传中...' : label}
      </button>
    </>
  );
}
